import { useRecoilValue, useRecoilState } from "recoil";
import { createContext, useContext } from "react";
import {
  activeScopeState,
  scopesState,
  scopePreferenceState,
  userInfoState,
} from "../global-state";
import { switchScope } from "../service/scope-service";
import { getUserInfo } from "../service/user-service";
import { useWebSocket } from "../ws/websocket";
import { useAppInterface } from "../app/app-interface-provider";
import dayjs from "dayjs";

// Create context for custom format function
const CustomFormatContext = createContext();

// Custom format function that considers scope preferences
export const createCustomFormat = (scopePreference) => {
  return (timestamp, now, future = false) => {
    const timestampDate = dayjs(timestamp);
    const diffMinutes = now.diff(timestampDate, "minute");
    const diffHours = now.diff(timestampDate, "hour");
    const absDiffMinutes = Math.abs(diffMinutes);
    const absDiffHours = Math.abs(diffHours);

    if (absDiffMinutes <= 1) {
      return diffMinutes > 0 || !future ? "Just now" : "In less than a minute";
    } else if (absDiffMinutes > 1 && absDiffMinutes < 60) {
      return diffMinutes > 0
        ? `${absDiffMinutes} ${absDiffMinutes === 1 ? "minute" : "minutes"} ago`
        : `In ${absDiffMinutes} ${absDiffMinutes === 1 ? "minute" : "minutes"}`;
    } else if (absDiffHours > 0 && absDiffHours < 24) {
      return diffHours > 0
        ? `${absDiffHours} ${absDiffHours === 1 ? "hour" : "hours"} ago`
        : `In ${absDiffHours} ${absDiffHours === 1 ? "hour" : "hours"}`;
    } else {
      // Use scope preference for date and time format, converting to dayjs format
      let dateFormat = scopePreference?.dateFormat || "yyyy-MM-dd";
      let timeFormat = scopePreference?.timeFormat || "HH:mm";

      // Convert format strings to dayjs format (uppercase Y and lowercase a)
      dateFormat = dateFormat.replace(/yyyy/g, "YYYY").replace(/dd/g, "DD");
      timeFormat = timeFormat.replace(/hh/g, "hh").replace(/a/g, "A");

      return timestampDate.format(`${dateFormat} ${timeFormat}`);
    }
  };
};

// Hook to use custom format function
export const useCustomFormat = () => {
  const context = useContext(CustomFormatContext);
  if (!context) {
    throw new Error("useCustomFormat must be used within AfterInitProvider");
  }
  return context;
};

// Hook for common user info and space management
export const useUserInfoManager = () => {
  const [userInfo, setUserInfo] = useRecoilState(userInfoState);
  const [activeScope] = useRecoilState(activeScopeState);
  const scopes = useRecoilValue(scopesState);
  const { sendAndWaitWs, changeActiveScope } = useWebSocket();
  const appInterface = useAppInterface();

  const fetchUserInfo = async () => {
    const resp = await getUserInfo(sendAndWaitWs);
    if (resp && !resp.error) {
      setUserInfo(resp);
      if (appInterface.isAvailable()) {
        await appInterface.saveUserInfo(resp);
      }
    }
    return resp;
  };

  const switchSpace = async (currentSpaceId, newSpaceId) => {
    if (currentSpaceId === newSpaceId) {
      return { success: true };
    }
    const resp = await switchScope(sendAndWaitWs, newSpaceId);
    if (resp && !resp.error) {
      changeActiveScope(newSpaceId);
      if (appInterface.isAvailable()) {
        await appInterface.saveScopes({
          scopes: scopes,
          activeScope: newSpaceId,
        });
      }
      localStorage.removeItem("filterParams-taskflow");
      localStorage.removeItem("filterParams-recurring");
      localStorage.removeItem("notifications");

      return { success: true };
    } else {
      return { success: false, error: resp.error };
    }
  };

  return {
    userInfo,
    activeScope,
    fetchUserInfo,
    switchSpace,
  };
};

// Component wrapper for after-init logic
export const AfterInitProvider = ({ children }) => {
  const scopePreference = useRecoilValue(scopePreferenceState);
  const activeScope = useRecoilValue(activeScopeState);

  // Create custom format function that uses current scope preferences
  const customFormat = createCustomFormat(scopePreference?.[activeScope]);

  return (
    <CustomFormatContext.Provider value={customFormat}>
      {children}
    </CustomFormatContext.Provider>
  );
};
